import { useState } from "react";
import { ScrollToTop } from "./ScrollToTop";
import { usePageMeta } from "../utils/usePageMeta";

type RegionId = "occident" | "asia" | "independents" | "xina";

interface Brand {
  name: string;
  logo?: string;
  role: string;
  note: string;
}

interface Division {
  title: string;
  brands: Brand[];
}

interface Stake {
  target: string;
  pct: string;
  note?: string;
  /** "out" (per defecte): el grup té accions del target. "in": el target té accions del grup. */
  direction?: "out" | "in";
}

interface Group {
  name: string;
  flag: string;
  logo?: string;
  tagline: string;
  region: RegionId;
  intro: string;
  divisions: Division[];
  stakes?: Stake[];
  context?: string;
}

const regions: { id: RegionId; label: string; title: string; blurb: string }[] = [
  {
    id: "occident",
    label: "Occident",
    title: "Grups occidentals",
    blurb:
      "Els grups històrics d'Europa i els Estats Units. Concentren bona part del volum global, però tots estan reaccionant a la pressió de preus i de tecnologia elèctrica que arriba de la Xina.",
  },
  {
    id: "asia",
    label: "Japó, Corea i Índia",
    title: "Grups japonesos, coreans i indis",
    blurb:
      "Els grans fabricants asiàtics fora de la Xina. Estructures de keiretsu i chaebol, amb participacions creuades que lliguen entre si gairebé tot el sector japonès.",
  },
  {
    id: "independents",
    label: "Independents",
    title: "Independents i standalone",
    blurb:
      "Marques sense grup matriu que les controli. Algunes són realment autònomes (Tesla, Mazda), d'altres tenen accionariats repartits on cap soci mana del tot (Aston Martin).",
  },
  {
    id: "xina",
    label: "Xina",
    title: "Grups xinesos",
    blurb:
      "El bloc que està reconfigurant tot el sector. Ja són ~10,6% del mercat europeu (gener–maig 2026) i superen Tesla en volum a Europa. L'estructura de submarques és molt més fragmentada que a Occident.",
  },
];

const groupsData: Group[] = [
  {
    name: "Toyota Group",
    flag: "🇯🇵",
    logo: "toyota",
    tagline: "#1 mundial · ~11,3M unitats (FY2026)",
    region: "asia",
    intro:
      "El cas clàssic del keiretsu japonès: controla o té participacions en gairebé tot l'ecosistema automobilístic del país sense haver fusionat res formalment.",
    divisions: [
      {
        title: "Marques 100% pròpies",
        brands: [
          { name: "Toyota", logo: "toyota", role: "Mainstream global", note: "La marca principal del grup i la més venuda del món." },
          { name: "Lexus", logo: "lexus", role: "Luxe", note: "Creada el 1989 com a divisió premium; el referent de com fer una marca de luxe des de zero." },
          { name: "Daihatsu", logo: "daihatsu", role: "Kei cars i compactes", note: "Cotxes petits i barats. Subsidiària al 100% des del 2016 (abans hi tenia el 51,2%)." },
          { name: "Hino", logo: "hino", role: "Camions i autobusos", note: "La divisió de vehicle industrial del grup." },
        ],
      },
    ],
    stakes: [
      { target: "Subaru", pct: "~20%", note: "Accionista individual més gran; d'aquí surt el duet GR86/BRZ." },
      { target: "Mazda", pct: "~5%", note: "Amb planta conjunta als Estats Units." },
      { target: "Suzuki", pct: "~5%" },
      { target: "Toyota Industries", pct: "51,2%", note: "Proveïdor del grup." },
      { target: "Denso", pct: "~20%", note: "Proveïdor del grup." },
    ],
    context:
      "Fora de l'automòbil, Toyota també té inversions a Uber, Joby Aviation, Aurora i Isuzu.",
  },
  {
    name: "Volkswagen Group",
    flag: "🇩🇪",
    logo: "volkswagen",
    tagline: "#2 mundial · en plena reestructuració",
    region: "occident",
    intro:
      "El grup amb més marques d'Europa, des de l'entrada de gamma fins als hipercars, amb una estructura accionarial creuada peculiar entre Porsche SE i el mateix VW.",
    divisions: [
      {
        title: "Turismes",
        brands: [
          { name: "Volkswagen", logo: "volkswagen", role: "Mainstream", note: "La marca de volum i el centre de gravetat del grup." },
          { name: "Audi", logo: "audi", role: "Premium", note: "Comprada als anys 60; abans era de Daimler-Benz, que la tenia pràcticament aturada." },
          { name: "Porsche", logo: "porsche", role: "Esportius i luxe", note: "Integració completa el 2012, tot i mantenir l'estructura creuada Porsche SE ↔ VW." },
          { name: "Škoda", logo: "skoda", role: "Entrada de gamma", note: "Al 100% des del 2000; avui una de les marques més sòlides del grup." },
          { name: "SEAT", logo: "seat", role: "Mainstream", note: "Comprada entre el 1986 i el 1990 al govern espanyol." },
          { name: "Cupra", logo: "cupra", role: "Esportiva i EV", note: "Escissió esportiva de SEAT el 2018, avui amb identitat pròpia." },
          { name: "Bentley", logo: "bentley", role: "Ultra-luxe", note: "Comprada el 1998, en la mateixa operació que va acabar repartint Rolls-Royce amb BMW." },
          { name: "Lamborghini", logo: "lamborghini", role: "Supercars", note: "Sota el paraigua d'Audi." },
          { name: "Bugatti", logo: "bugatti", role: "Hipercars", note: "Ara dins la JV Bugatti-Rimac, on VW/Porsche són minoritaris i Rimac en té el control." },
        ],
      },
      {
        title: "Motos i camions",
        brands: [
          { name: "Ducati", role: "Motos", note: "Sota Audi." },
          { name: "Scania", logo: "scania", role: "Camions", note: "Dins la divisió Traton." },
          { name: "MAN", logo: "man", role: "Camions", note: "Dins la divisió Traton." },
          { name: "Navistar", logo: "navistar", role: "Camions", note: "Dins la divisió Traton." },
          { name: "International", logo: "international", role: "Camions", note: "Dins la divisió Traton." },
        ],
      },
    ],
    context:
      "És el grup dels 100.000 acomiadaments previstos fins al 2030 i el tancament de 4 fàbriques a Alemanya. El marge operatiu ha caigut al 2,8% (des del 5,9% el 2024) per la pressió xinesa.",
  },
  {
    name: "Stellantis",
    flag: "🇮🇹🇺🇸🇫🇷",
    logo: "peugeot",
    tagline: "14 marques · creat el 2021 (PSA + FCA)",
    region: "occident",
    intro:
      "Fusió 50-50 del gener del 2021 entre el grup francès PSA (Peugeot) i l'italoamericà FCA (Fiat Chrysler). Les tres arrels encara s'endevinen clarament en el catàleg.",
    divisions: [
      {
        title: "Arrel ex-PSA (francesa i alemanya)",
        brands: [
          { name: "Peugeot", logo: "peugeot", role: "Mainstream", note: "La marca de volum francesa del grup." },
          { name: "Citroën", logo: "citroen", role: "Mainstream", note: "Coneguda pels dissenys trencadors." },
          { name: "DS Automobiles", logo: "ds", role: "Luxe", note: "Nascuda com a submarca de Citroën el 2010; marca independent des del 2015." },
          { name: "Opel", logo: "opel", role: "Mainstream", note: "Alemanya; va ser de General Motors fins al 2017." },
          { name: "Vauxhall", logo: "vauxhall", role: "Mainstream", note: "La bessona britànica d'Opel." },
        ],
      },
      {
        title: "Arrel ex-FCA italiana",
        brands: [
          { name: "Fiat", logo: "fiat", role: "Volum", note: "Cotxes petits; el gruix del volum italià." },
          { name: "Alfa Romeo", logo: "alfa-romeo", role: "Esportiu i premium", note: "La marca passional del grup." },
          { name: "Lancia", logo: "lancia", role: "En relançament", note: "Gairebé només present a Itàlia, ara en procés de retorn." },
          { name: "Maserati", logo: "maserati", role: "Luxe", note: "El cas crític del grup: ~7.900 vendes el 2025 i pèrdues. Stellantis negocia amb «dos socis» tecnològics." },
          { name: "Abarth", logo: "abarth", role: "Esportiva", note: "La divisió de prestacions de Fiat." },
        ],
      },
      {
        title: "Arrel ex-FCA americana",
        brands: [
          { name: "Jeep", logo: "jeep", role: "4×4 i SUV", note: "D'origen militar (1941); la marca més global de la branca americana." },
          { name: "Ram", logo: "ram", role: "Pickups", note: "Escindida de Dodge per centrar-se en pickups." },
          { name: "Chrysler", logo: "chrysler", role: "Sedans", note: "Gamma molt reduïda avui." },
          { name: "Dodge", logo: "dodge", role: "Muscle i performance", note: "Cotxes de prestacions americans." },
        ],
      },
    ],
    stakes: [
      { target: "Leapmotor", pct: "~21%", note: "Major accionista individual, més la JV Leapmotor International (51/49) per vendre'ls fora de la Xina. És la via clau per accedir a tecnologia EV barata." },
    ],
    context:
      "Stellantis va tancar el 2025 amb −26.300 M$ de pèrdua neta. El nou CEO Antonio Filosa (des del 2025) està reorientant l'estratègia, amb John Elkann de president.",
  },
  {
    name: "Hyundai Motor Group",
    flag: "🇰🇷",
    logo: "hyundai",
    tagline: "El chaebol de l'automòbil coreà",
    region: "asia",
    intro:
      "Estructura de participacions creuades circulars típica dels chaebols coreans: Hyundai Mobis → Hyundai Motor → Kia → Mobis.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Hyundai", logo: "hyundai", role: "Mainstream", note: "Gamma cada cop més premium i tecnològica." },
          { name: "Kia", logo: "kia", role: "Mainstream", note: "Hyundai n'és l'accionista de control." },
          { name: "Genesis", logo: "genesis", role: "Luxe", note: "Spin-off de la gamma alta de Hyundai el 2015." },
        ],
      },
    ],
  },
  {
    name: "General Motors",
    flag: "🇺🇸",
    logo: "general-motors",
    tagline: "El gegant americà",
    region: "occident",
    intro:
      "Quatre marques molt delimitades per nínxol dins del mercat nord-americà, amb Buick jugant un paper especial a la Xina.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Chevrolet", logo: "chevrolet", role: "Volum", note: "La marca insígnia del grup." },
          { name: "GMC", logo: "gmc", role: "Pickups i utilitaris", note: "Vehicles de treball i SUV grans." },
          { name: "Cadillac", logo: "cadillac", role: "Luxe", note: "Entra a la F1 el 2026 com a 11è equip, inicialment amb motors Ferrari." },
          { name: "Buick", logo: "buick", role: "Premium accessible", note: "Especialment fort a la Xina." },
        ],
      },
    ],
  },
  {
    name: "Ford Motor Company",
    flag: "🇺🇸",
    logo: "ford",
    tagline: "Dues marques, focus total",
    region: "occident",
    intro:
      "Un dels catàlegs més curts entre els grans grups, després d'haver desmuntat el seu imperi de marques premium.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Ford", logo: "ford", role: "Volum global", note: "La marca principal, avui molt centrada en pickups i SUV." },
          { name: "Lincoln", logo: "lincoln", role: "Luxe", note: "La divisió premium americana." },
        ],
      },
    ],
    context:
      "Als anys 2000 Ford tenia Jaguar, Land Rover, Volvo i Aston Martin sota el «Premier Automotive Group», però ho va vendre tot: JLR a Tata (2008) i Volvo a Geely (2010).",
  },
  {
    name: "Honda",
    flag: "🇯🇵",
    logo: "honda",
    tagline: "Independent · aliança tecnològica amb Nissan",
    region: "asia",
    intro:
      "Un dels pocs grans fabricants que continua sol, i el major fabricant de motos del món.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Honda", logo: "honda", role: "Mainstream i motos", note: "Major fabricant de motocicletes del món." },
          { name: "Acura", logo: "acura", role: "Luxe", note: "La primera marca de luxe japonesa (1986), abans que Lexus i Infiniti." },
        ],
      },
    ],
    context:
      "La fusió Honda-Nissan-Mitsubishi va fracassar el 2025 per desacord de control (Honda volia absorbir Nissan). Ara tanquen un acord tecnològic —ECU i software compartits cap al 2029-30—, no accionarial. Honda ha tingut un primer semestre del 2026 dur i ha cancel·lat tots els seus programes EV propis.",
  },
  {
    name: "Aliança Renault–Nissan–Mitsubishi",
    flag: "🇫🇷🇯🇵",
    logo: "renault",
    tagline: "Aliança, no fusió · des del 1999",
    region: "occident",
    intro:
      "Tres fabricants que comparteixen plataformes i participacions creuades sense fusionar-se mai. L'estructura es va rebalancejar el 2023 per equilibrar el pes de Renault i Nissan.",
    divisions: [
      {
        title: "Renault Group 🇫🇷",
        brands: [
          { name: "Renault", logo: "renault", role: "Mainstream", note: "La marca principal francesa." },
          { name: "Dacia", logo: "dacia", role: "Low-cost", note: "D'arrel romanesa; el motor de rendibilitat del grup." },
          { name: "Alpine", logo: "alpine", role: "Esportius", note: "Halo brand, revifada el 2017." },
          { name: "Mobilize", role: "Mobilitat i serveis", note: "La branca de serveis i vehicles d'ús compartit." },
          { name: "Ampere", role: "EV i software", note: "Divisió EV pura; pot fabricar per a tercers, com el Twingo per a Nissan." },
          { name: "Horse", role: "Motors tèrmics", note: "JV 50/50 amb Geely per als motors de combustió." },
        ],
      },
      {
        title: "Nissan 🇯🇵",
        brands: [
          { name: "Nissan", logo: "nissan", role: "Mainstream", note: "La marca de volum japonesa de l'aliança." },
          { name: "Infiniti", logo: "infiniti", role: "Luxe", note: "Creada el 1989 per competir amb Lexus i Acura." },
        ],
      },
      {
        title: "Mitsubishi Motors 🇯🇵",
        brands: [
          { name: "Mitsubishi", logo: "mitsubishi", role: "Soci júnior", note: "Nissan hi va entrar amb un 34% el 2016." },
        ],
      },
    ],
    stakes: [
      { target: "Renault → Nissan", pct: "~15%", note: "Directe, baixat des del 43% el 2023; la resta va a un trust francès amb drets econòmics però vot neutre." },
      { target: "Nissan → Renault", pct: "~15%" },
      { target: "Geely", pct: "—", note: "No de l'aliança directament, sinó dels seus projectes: Renault Korea ~34% i la JV Horse.", direction: "in" },
    ],
    context:
      "El 2025 Renault i Nissan van acordar poder baixar el mínim mutu de participació al 10% per alliberar capital.",
  },
  {
    name: "BMW Group",
    flag: "🇩🇪",
    logo: "bmw",
    tagline: "Premium alemany",
    region: "occident",
    intro:
      "Tres marques molt ben separades per segment, des del compacte premium fins a l'ultra-luxe.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "BMW", logo: "bmw", role: "Premium", note: "La marca principal del grup." },
          { name: "MINI", logo: "mini", role: "Compactes premium", note: "Comprada el 1994; marca pròpia des del 2001." },
          { name: "Rolls-Royce", logo: "rolls-royce", role: "Ultra-luxe", note: "BMW en té els drets per als cotxes des del 2003. L'empresa aeronàutica Rolls-Royce plc és una altra cosa." },
        ],
      },
    ],
  },
  {
    name: "Mercedes-Benz Group",
    flag: "🇩🇪",
    logo: "mercedes-benz",
    tagline: "Abans Daimler",
    region: "occident",
    intro:
      "Després d'escindir els camions el 2021, el grup s'ha concentrat en el turisme premium i de luxe.",
    divisions: [
      {
        title: "Marques i submarques",
        brands: [
          { name: "Mercedes-Benz", logo: "mercedes-benz", role: "Premium i luxe", note: "La marca principal." },
          { name: "Mercedes-AMG", logo: "mercedes-amg", role: "Alt rendiment", note: "La divisió de prestacions." },
          { name: "Mercedes-Maybach", logo: "maybach", role: "Ultra-luxe", note: "Submarca per a la gamma més alta." },
          { name: "smart", logo: "smart", role: "Microcotxes EV", note: "Ara és una JV 50/50 amb Geely." },
        ],
      },
    ],
    stakes: [
      { target: "Daimler Truck", pct: "escindida", note: "Empresa separada des del 2021: Freightliner, camions Mercedes, Setra, etc." },
    ],
    context:
      "Qui té accions de Mercedes: Geely ~9,7% (des del 2018, va arribar a ser el major accionista) i la xinesa BAIC ~9,98%.",
  },
  {
    name: "Tata Motors",
    flag: "🇮🇳",
    logo: "tata",
    tagline: "Tata + Jaguar Land Rover",
    region: "asia",
    intro:
      "JLR va ser la gran història d'èxit de Tata: Jaguar no va donar mai beneficis sota Ford i va florir sota propietat índia.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Tata", logo: "tata", role: "Mainstream indi", note: "Cotxes, camions i autobusos." },
          { name: "Jaguar", logo: "jaguar", role: "Luxe britànic", note: "De Ford fins al 2008; en ple relançament 100% elèctric i amb un rebranding polèmic." },
          { name: "Land Rover", logo: "land-rover", role: "4×4 premium", note: "De Ford fins al 2008; inclou Range Rover." },
        ],
      },
    ],
  },

  // ---- Independents ----
  {
    name: "Tesla",
    flag: "🇺🇸",
    logo: "tesla",
    tagline: "100% EV · independent",
    region: "independents",
    intro: "Independent, dirigida per Elon Musk. Cap grup automobilístic tradicional hi té participació.",
    divisions: [],
  },
  {
    name: "Suzuki",
    flag: "🇯🇵",
    logo: "suzuki",
    tagline: "Forta a l'Índia i el Japó",
    region: "independents",
    intro:
      "Especialista en cotxes petits i kei cars. Volkswagen en va tenir ~19,9%, que Suzuki va recomprar. Avui Toyota hi té ~5%.",
    divisions: [],
  },
  {
    name: "Mazda",
    flag: "🇯🇵",
    logo: "mazda",
    tagline: "Independent · Toyota ~5%",
    region: "independents",
    intro: "Independent, amb Toyota com a soci minoritari (~5%) i una planta conjunta als Estats Units.",
    divisions: [],
  },
  {
    name: "Subaru",
    flag: "🇯🇵",
    logo: "subaru",
    tagline: "Independent · Toyota ~20%",
    region: "independents",
    intro: "Independent, però Toyota n'és el major accionista amb ~20%. D'aquesta relació en surten el GR86 i el BRZ.",
    divisions: [],
  },
  {
    name: "Ferrari",
    flag: "🇮🇹",
    logo: "ferrari",
    tagline: "Spin-off de FCA (2016) · cotitza com a RACE",
    region: "independents",
    intro:
      "Escindida de FCA/Fiat el 2016 i cotitza sola a Nova York i Milà. Fiat n'havia tingut una part del 1969 al 2014. Els seus marges de luxe la fan un negoci molt diferent del dels generalistes.",
    divisions: [],
  },
  {
    name: "Aston Martin",
    flag: "🇬🇧",
    logo: "aston-martin",
    tagline: "Cotitzada · accionariat repartit",
    region: "independents",
    intro: "Independent en el sentit que cap grup automobilístic la controla, però amb un accionariat molt repartit.",
    divisions: [],
    stakes: [
      { target: "Yew Tree Consortium", pct: "major accionista", note: "El vehicle inversor de Lawrence Stroll.", direction: "in" },
      { target: "Public Investment Fund (Aràbia Saudita)", pct: "~18%", direction: "in" },
      { target: "Geely", pct: "~17%", note: "Tercer accionista, amb seient no executiu al consell.", direction: "in" },
      { target: "Mercedes-Benz", pct: "minoritari", note: "Participació tecnològica, per sota de Geely.", direction: "in" },
    ],
  },
  {
    name: "McLaren",
    flag: "🇬🇧",
    logo: "mclaren",
    tagline: "Supercars + F1",
    region: "independents",
    intro: "Capital de Bahrain (Mumtalakat), amb inversió de CYVN d'Abu Dhabi.",
    divisions: [],
  },
  {
    name: "Rivian",
    flag: "🇺🇸",
    logo: "rivian",
    tagline: "EV · independent",
    region: "independents",
    intro: "Fabricant EV independent, amb una JV de software amb Volkswagen des del 2024.",
    divisions: [],
  },
  {
    name: "Lucid",
    flag: "🇺🇸",
    logo: "lucid",
    tagline: "EV de luxe",
    region: "independents",
    intro: "Majoritàriament propietat del PIF saudita.",
    divisions: [],
  },
  {
    name: "VinFast",
    flag: "🇻🇳",
    logo: "vinfast",
    tagline: "EV vietnamita",
    region: "independents",
    intro: "Del conglomerat vietnamita Vingroup.",
    divisions: [],
  },

  // ---- Xina ----
  {
    name: "BYD",
    flag: "🇨🇳",
    logo: "byd",
    tagline: "Líder mundial en NEV",
    region: "xina",
    intro:
      "Integració vertical total: fabrica les seves pròpies bateries Blade. Té una estructura de marques diferent dins i fora de la Xina.",
    divisions: [
      {
        title: "Estructura a la Xina",
        brands: [
          { name: "BYD", logo: "byd", role: "Marca principal", note: "Amb les sèries Dynasty i Ocean." },
          { name: "Denza", role: "Premium", note: "Originàriament una JV amb Mercedes; ara BYD en té el control." },
          { name: "Fangchengbao", role: "4×4 i esportius", note: "方程豹. Bao 5 i Bao 8, i un concept supercar (Formula X) previst per al 2027." },
          { name: "Yangwang", role: "Ultra-luxe", note: "El U8 que flota a l'aigua i el supercar U9." },
        ],
      },
    ],
    context:
      "Fora de la Xina simplifica: Dynasty i Ocean van tots sota la marca BYD, Denza i Fangchengbao operen juntes, i Yangwang va a part com a marca de luxe.",
  },
  {
    name: "Geely Holding",
    flag: "🇨🇳",
    logo: "geely",
    tagline: "El «col·leccionista» global",
    region: "xina",
    intro:
      "El grup xinès amb més tentacles a Occident. Va començar comprant Volvo a Ford el 2010 per 1.800 M$. És qui cal vigilar per entendre com la Xina entra a Occident: no sempre comprant, sinó amb minories estratègiques i JVs.",
    divisions: [
      {
        title: "Marques que controla",
        brands: [
          { name: "Geely Auto", logo: "geely", role: "Marca principal", note: "La marca de volum xinesa." },
          { name: "Volvo Cars", logo: "volvo", role: "Premium suec", note: "~78,7%; recuperada de Ford el 2010." },
          { name: "Polestar", logo: "polestar", role: "EV performance", note: "Escissió de Volvo." },
          { name: "Lotus", logo: "lotus", role: "Esportius britànics", note: "51%, via Proton." },
          { name: "Zeekr", logo: "zeekr", role: "EV premium", note: "La punta de llança tecnològica del grup." },
          { name: "Lynk & Co", logo: "lynk-co", role: "Orientada a joves", note: "JV amb Volvo." },
          { name: "smart", logo: "smart", role: "Microcotxes EV", note: "JV 50/50 amb Mercedes." },
          { name: "Proton", logo: "proton", role: "Malaia", note: "~49,9%." },
          { name: "Livan", logo: "livan", role: "Submarca xinesa", note: "Una de les marques domèstiques del grup." },
          { name: "Radar", role: "Submarca xinesa", note: "Pickups i vehicles d'aventura elèctrics." },
          { name: "Galaxy", role: "Submarca xinesa", note: "La gamma electrificada de Geely Auto." },
        ],
      },
    ],
    stakes: [
      { target: "Mercedes-Benz Group", pct: "~9,7%" },
      { target: "Aston Martin", pct: "~17%", note: "Tercer accionista." },
      { target: "Renault Korea", pct: "~34%" },
      { target: "Volvo AB", pct: "major accionista", note: "Els camions: una empresa diferent de Volvo Cars." },
      { target: "Horse (JV amb Renault)", pct: "50%", note: "Motors tèrmics." },
    ],
  },
  {
    name: "SAIC",
    flag: "🇨🇳",
    logo: "saic",
    tagline: "El més gran per volum · estatal",
    region: "xina",
    intro: "Grup estatal de Xangai, amb JVs històriques amb Volkswagen i General Motors a la Xina.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "MG", logo: "mg", role: "Punta de llança a Europa", note: "Marca britànica revifada; la més visible del grup fora de la Xina." },
          { name: "Roewe", logo: "roewe", role: "Mainstream xinès", note: "La marca domèstica de volum." },
          { name: "Maxus / LDV", logo: "maxus", role: "Comercials", note: "Furgonetes i vehicles de treball." },
          { name: "IM Motors", role: "EV premium", note: "智己. La marca tecnològica del grup." },
          { name: "Wuling", logo: "wuling", role: "Microcotxes", note: "Via la JV SAIC-GM-Wuling; cotxes ultra-barats." },
          { name: "Baojun", logo: "baojun", role: "Microcotxes", note: "També dins la JV SAIC-GM-Wuling." },
        ],
      },
    ],
  },
  {
    name: "Chery",
    flag: "🇨🇳",
    logo: "chery",
    tagline: "El rei de l'exportació",
    region: "xina",
    intro: "Líder xinès en exportacions, amb 393.300 unitats el primer trimestre del 2026.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Chery", logo: "chery", role: "Marca principal", note: "La marca domèstica del grup." },
          { name: "Omoda", logo: "omoda", role: "Exportació", note: "Una de les que estan arribant a Europa." },
          { name: "Jaecoo", logo: "jaecoo", role: "Exportació", note: "Germana d'Omoda, amb enfocament més SUV." },
          { name: "Jetour", role: "SUV i off-road", note: "Molt forta al mercat domèstic." },
          { name: "Exeed", logo: "exeed", role: "Premium", note: "La gamma alta del grup." },
          { name: "Luxeed", role: "EV", note: "En col·laboració amb Huawei." },
          { name: "iCar", role: "EV", note: "Submarca elèctrica." },
          { name: "Fulwin", role: "EV", note: "Submarca elèctrica." },
          { name: "Rely", role: "Submarca", note: "Marca secundària del grup." },
        ],
      },
    ],
  },
  {
    name: "Changan",
    flag: "🇨🇳",
    logo: "changan",
    tagline: "Estatal · fort creixement EV",
    region: "xina",
    intro: "Grup estatal amb una expansió elèctrica ràpida, sovint de la mà de Huawei i CATL.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Changan", logo: "changan", role: "Marca principal", note: "La marca de volum." },
          { name: "Deepal", role: "EV", note: "深蓝. Elèctrics de gamma mitjana." },
          { name: "Avatr", role: "EV premium", note: "Desenvolupada amb Huawei i CATL." },
          { name: "Oshan", role: "Submarca", note: "Vehicles familiars i comercials lleugers." },
          { name: "Nevo", role: "Submarca", note: "Gamma electrificada." },
        ],
      },
    ],
  },
  {
    name: "Great Wall Motors",
    flag: "🇨🇳",
    logo: "great-wall",
    tagline: "GWM · especialista en SUV i 4×4",
    region: "xina",
    intro: "El grup xinès més centrat en SUV, tot terreny i pickups.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Haval", logo: "haval", role: "SUV", note: "El H6 és dels més venuts de la Xina." },
          { name: "Tank", role: "4×4 premium", note: "Off-road de gamma alta." },
          { name: "Wey", logo: "wey", role: "Premium", note: "La marca premium del grup." },
          { name: "Ora", role: "EV urbans", note: "Elèctrics petits de disseny retro." },
          { name: "Poer", role: "Pickups", note: "La divisió de vehicles de treball." },
        ],
      },
    ],
  },
  {
    name: "Dongfeng",
    flag: "🇨🇳",
    logo: "dongfeng",
    tagline: "Estatal",
    region: "xina",
    intro: "Grup estatal amb JVs amb Honda, Nissan i, històricament, Stellantis/PSA.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Voyah", role: "EV premium", note: "La marca elèctrica de gamma alta." },
          { name: "M-Hero", role: "4×4 elèctrics extrems", note: "Off-road radical electrificat." },
          { name: "Venucia", logo: "venucia", role: "Submarca", note: "Nascuda de la JV amb Nissan." },
          { name: "Aeolus", role: "Submarca", note: "Marca de volum domèstica." },
          { name: "Lingxi", role: "Submarca", note: "Elèctrics compactes." },
          { name: "Nammi", role: "Submarca", note: "Elèctrics urbans." },
        ],
      },
    ],
  },
  {
    name: "BAIC",
    flag: "🇨🇳",
    logo: "baic",
    tagline: "Estatal · accionista de Mercedes",
    region: "xina",
    intro: "Grup estatal de Pequín, conegut sobretot fora de la Xina per la seva participació a Mercedes-Benz.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "Arcfox", logo: "arcfox", role: "EV premium", note: "La marca elèctrica del grup." },
          { name: "Stelato", role: "EV luxe", note: "享界. Desenvolupada amb Huawei." },
          { name: "Foton", logo: "foton", role: "Comercials i camions", note: "La divisió industrial." },
        ],
      },
    ],
    stakes: [{ target: "Mercedes-Benz", pct: "~9,98%" }],
  },
  {
    name: "JAC",
    flag: "🇨🇳",
    logo: "jac",
    tagline: "Mainstream i comercials",
    region: "xina",
    intro: "Grup mitjà que ha trobat una segona vida en el luxe elèctric de la mà de Huawei.",
    divisions: [
      {
        title: "Marques del grup",
        brands: [
          { name: "JAC", logo: "jac", role: "Mainstream i comercials", note: "La marca principal." },
          { name: "Maextro", role: "Ultra-luxe", note: "尊界. Amb Huawei; la berlina S800. Sona una possible col·laboració amb Maserati." },
          { name: "Evo", logo: "evo", role: "Submarca", note: "Gamma elèctrica accessible." },
          { name: "Nord", role: "Submarca", note: "Marca secundària del grup." },
        ],
      },
    ],
  },
  {
    name: "Startups xineses EV",
    flag: "🇨🇳",
    logo: "nio",
    tagline: "Nio, Xpeng, Li Auto, Xiaomi…",
    region: "xina",
    intro:
      "La nova generació de fabricants nascuts directament elèctrics, sense herència industrial. Els analistes esperen que molts d'aquests noms no sobrevisquin la propera dècada.",
    divisions: [
      {
        title: "Les principals",
        brands: [
          { name: "Nio", logo: "nio", role: "EV premium", note: "Pionera del battery swap. Submarques: Onvo (mainstream) i Firefly (urbà petit)." },
          { name: "Xpeng", logo: "xpeng", role: "EV tech i AI", note: "Rival directe de Tesla, amb el P7 i el G6." },
          { name: "Li Auto", logo: "lixiang", role: "SUV EREV", note: "理想. Range-extenders; una de les poques molt rendibles." },
          { name: "Xiaomi Auto", role: "EV", note: "El nouvingut del gegant tecnològic (SU7, YU7), amb un creixement molt ràpid." },
          { name: "Leapmotor", logo: "leapmotor", role: "EV", note: "Ja no és del tot independent: Stellantis hi té ~21% i FAW ~5%." },
        ],
      },
      {
        title: "Sota pressió",
        brands: [
          { name: "Neta", role: "EV", note: "Més petita i en dificultats financeres." },
          { name: "Aiways", logo: "aiways", role: "EV", note: "Presència europea molt reduïda." },
          { name: "Rox", role: "EV", note: "Marca petita, sota pressió." },
        ],
      },
    ],
    context:
      "El mercat xinès està en consolidació brutal: la guerra de preus està migrant cap a una «guerra de valor» basada en tecnologia, ecosistema i marca.",
  },
];

const crossHoldings: { holder: string; holderLogo?: string; target: string; pct: string }[] = [
  { holder: "Geely", holderLogo: "geely", target: "Volvo Cars", pct: "~78,7% (control)" },
  { holder: "Geely", holderLogo: "geely", target: "Aston Martin", pct: "~17%" },
  { holder: "Geely", holderLogo: "geely", target: "Mercedes-Benz", pct: "~9,7%" },
  { holder: "BAIC", holderLogo: "baic", target: "Mercedes-Benz", pct: "~10%" },
  { holder: "Toyota", holderLogo: "toyota", target: "Subaru", pct: "~20%" },
  { holder: "Toyota", holderLogo: "toyota", target: "Mazda", pct: "~5%" },
  { holder: "Toyota", holderLogo: "toyota", target: "Suzuki", pct: "~5%" },
  { holder: "Renault", holderLogo: "renault", target: "Nissan", pct: "~15%" },
  { holder: "Nissan", holderLogo: "nissan", target: "Renault", pct: "~15%" },
  { holder: "Nissan", holderLogo: "nissan", target: "Mitsubishi", pct: "~24-34%" },
  { holder: "Stellantis", holderLogo: "peugeot", target: "Leapmotor", pct: "~21%" },
  { holder: "Mercedes-Benz", holderLogo: "mercedes-benz", target: "Aston Martin", pct: "minoritari" },
  { holder: "PIF (Aràbia Saudita)", target: "Aston Martin / Lucid", pct: "~18% / control" },
];

// A mòbil el % va entre parèntesis darrere la marca, així que els aplanem
// per no acabar amb parèntesis niats: "~78,7% (control)" → "~78,7%, control".
const inlinePct = (pct: string) => pct.replace(/\s*\((.+)\)\s*$/, ", $1");

const slugify = (name: string) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const logoUrl = (slug: string) => `/logos/${slug}-logo.png`;

/** Brand logo with a monogram fallback for brands we don't have an image for. */
const BrandMark = ({
  name,
  logo,
  className = "",
}: {
  name: string;
  logo?: string;
  className?: string;
}) => {
  const [failed, setFailed] = useState(false);

  if (!logo || failed) {
    return (
      <span
        className={`flex items-center justify-center shrink-0 rounded-full bg-secondary border border-border font-semibold text-muted-foreground ${className}`}
        aria-hidden="true"
      >
        {name.charAt(0)}
      </span>
    );
  }

  return (
    <img
      src={logoUrl(logo)}
      alt=""
      aria-hidden="true"
      loading="lazy"
      onError={() => setFailed(true)}
      className={`shrink-0 rounded-full object-contain bg-white border border-black/10 dark:border-white/15 p-1 ${className}`}
    />
  );
};

export const Grups = () => {
  usePageMeta({
    title: "Grups automobilístics | Cotx.es",
    description:
      "Mapa complet dels grups automobilístics mundials: quines marques té cada grup i quines participacions creuades hi ha entre ells.",
  });

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 sm:px-10 py-12 max-w-6xl">
          <header className="mb-10 sm:mb-12 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-foreground text-balance">
              Grups automobilístics
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground font-light max-w-2xl mx-auto leading-relaxed text-pretty">
              Qui és propietari de qui. Marques, submarques i les participacions creuades
              que sovint es passen per alt. Dades de juliol del 2026; els percentatges són
              aproximats i canvien sovint.
            </p>
          </header>

          {/* Jump nav, agrupada per regió */}
          <nav className="mb-12 sm:mb-16 space-y-4" aria-label="Índex de grups">
            {regions.map((region) => (
              <div key={region.id}>
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 sm:mb-2">
                  {region.label}
                </p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {groupsData
                    .filter((g) => g.region === region.id)
                    .map((group) => (
                      <a
                        key={group.name}
                        href={`#${slugify(group.name)}`}
                        className="flex items-center gap-1.5 sm:gap-2 pl-1 pr-2.5 py-1 sm:pl-1.5 sm:pr-3.5 sm:py-1.5 rounded-full bg-card border border-border text-xs sm:text-sm font-medium text-foreground hover:bg-muted hover:border-foreground/20 transition-colors"
                      >
                        <BrandMark
                          name={group.name}
                          logo={group.logo}
                          className="w-5 h-5 sm:w-6 sm:h-6 text-[10px] sm:text-[11px]"
                        />
                        {group.name}
                      </a>
                    ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Seccions per regió */}
          <div className="space-y-20">
            {regions.map((region) => (
              <section key={region.id} id={region.id} className="scroll-mt-6">
                <div className="mb-6 sm:mb-8 border-l-4 border-foreground/20 pl-4">
                  <h2 className="text-xl sm:text-3xl font-bold text-foreground mb-2 text-balance">
                    {region.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl text-pretty">
                    {region.blurb}
                  </p>
                </div>

                <div className="space-y-8">
                  {groupsData
                    .filter((g) => g.region === region.id)
                    .map((group) => (
                      <article
                        key={group.name}
                        id={slugify(group.name)}
                        className="bg-card rounded-2xl border border-border p-5 sm:p-8 shadow-sm scroll-mt-6"
                      >
                        <div className="flex items-start gap-3 sm:gap-4 mb-5">
                          <BrandMark
                            name={group.name}
                            logo={group.logo}
                            className="w-12 h-12 sm:w-16 sm:h-16 text-xl"
                          />
                          <div className="min-w-0">
                            <h3 className="text-lg sm:text-2xl font-bold text-foreground leading-tight text-balance">
                              {group.name}
                              <span className="ml-2">{group.flag}</span>
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-1 text-pretty">
                              {group.tagline}
                            </p>
                          </div>
                        </div>

                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6 text-pretty">
                          {group.intro}
                        </p>

                        {group.divisions.map((division) => (
                          <div key={division.title} className="mb-6 border-t border-border pt-6">
                            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">
                              {division.title}
                            </h4>
                            <ul className="grid gap-3 sm:grid-cols-2">
                              {division.brands.map((brand) => (
                                <li
                                  key={brand.name}
                                  className="flex gap-3 bg-card rounded-xl p-3.5 sm:p-4 border border-border"
                                >
                                  <BrandMark
                                    name={brand.name}
                                    logo={brand.logo}
                                    className="w-9 h-9 sm:w-10 sm:h-10 text-sm"
                                  />
                                  <div className="min-w-0">
                                    <p className="font-semibold text-foreground leading-tight text-balance">
                                      {brand.name}
                                    </p>
                                    <p className="text-xs font-medium text-foreground/60 mt-0.5">
                                      {brand.role}
                                    </p>
                                    <p className="text-sm text-muted-foreground leading-relaxed mt-1.5 text-pretty">
                                      {brand.note}
                                    </p>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}

                        {(["out", "in"] as const).map((direction) => {
                          const stakes = (group.stakes ?? []).filter(
                            (stake) => (stake.direction ?? "out") === direction,
                          );
                          if (stakes.length === 0) return null;

                          return (
                            <div key={direction} className="mb-6 border-t border-border pt-6">
                              <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">
                                {direction === "out"
                                  ? "Accions en altres empreses"
                                  : "Els seus accionistes"}
                              </h4>
                              <ul className="space-y-2">
                                {stakes.map((stake) => (
                                  <li
                                    key={stake.target}
                                    className="text-sm text-muted-foreground leading-relaxed text-pretty"
                                  >
                                    <span className="font-semibold text-foreground">
                                      {stake.target}
                                    </span>
                                    <span className="mx-2 inline-block rounded-md bg-card border border-border px-1.5 py-0.5 text-xs font-medium text-foreground">
                                      {stake.pct}
                                    </span>
                                    {stake.note}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}

                        {group.context && (
                          <p className="border-t border-border pt-6 text-sm text-muted-foreground leading-relaxed text-pretty">
                            <span className="font-semibold text-foreground">Context: </span>
                            {group.context}
                          </p>
                        )}
                      </article>
                    ))}
                </div>
              </section>
            ))}
          </div>

          {/* Resum de participacions creuades */}
          <section id="participacions-creuades" className="mt-20 scroll-mt-6">
            <div className="mb-6 sm:mb-8 border-l-4 border-foreground/20 pl-4">
              <h2 className="text-xl sm:text-3xl font-bold text-foreground mb-2 text-balance">
                Participacions creuades
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl text-pretty">
                El resum del que sovint es passa per alt: qui té accions de qui, encara que
                les marques semblin rivals.
              </p>
            </div>

            <div className="bg-card rounded-2xl border border-border shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-3 sm:px-5 py-3 font-semibold text-foreground">Qui</th>
                    <th className="px-3 sm:px-5 py-3 font-semibold text-foreground">
                      <span className="sm:hidden">Té participacions en</span>
                      <span className="hidden sm:inline">Té stake en</span>
                    </th>
                    <th className="hidden sm:table-cell px-5 py-3 font-semibold text-foreground text-right">
                      % aprox.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {crossHoldings.map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-3 sm:px-5 py-3">
                        <span className="flex items-center gap-2 font-medium text-foreground">
                          <BrandMark
                            name={row.holder}
                            logo={row.holderLogo}
                            className="w-7 h-7 shrink-0 text-[11px]"
                          />
                          {row.holder}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-3 text-muted-foreground text-pretty">
                        {row.target}
                        <span className="sm:hidden font-medium text-foreground">
                          {" "}
                          ({inlinePct(row.pct)})
                        </span>
                      </td>
                      <td className="hidden sm:table-cell px-5 py-3 text-right font-medium text-foreground whitespace-nowrap">
                        {row.pct}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <p className="mt-12 text-xs text-muted-foreground leading-relaxed text-center max-w-2xl mx-auto text-pretty">
            Dades de juliol del 2026.
          </p>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};
